// The read-side data layer. Each hook fetches the REAL C1–C8 route when live, or returns a typed
// fixture when USE_MSW (dev without a Clerk key). Writes live in feature slices, not here. Swapping a
// surface to live = deleting its fixture branch; the shapes line up 1:1 (seed-data.js contract).

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { USE_MSW } from '../config';
import { apiGet, apiWrite, newIdempotencyKey } from './client';
import { ROUTES } from './routes';
import type { Enveloped } from './envelope';
import type { XcpChat, XcpEvent, XcpMessage, XcpProject, XcpSource } from '../fixtures/types';
import {
  FIXTURE_CHATS,
  FIXTURE_EVENTS,
  FIXTURE_PROJECTS,
  FIXTURE_SOURCES,
  FIXTURE_THREADS,
} from '../fixtures/seed';

/** Resolve to a fixture in dev/MSW mode, else run the live loader. Fixtures ship the same envelope shape. */
async function fixtureOr<T>(fixture: T, live: () => Promise<Enveloped<T>>): Promise<Enveloped<T>> {
  if (USE_MSW) return Promise.resolve({ data: fixture, data_class: 'starter' });
  return live();
}

export function useProjects(): UseQueryResult<Enveloped<XcpProject[]>> {
  return useQuery({
    queryKey: ['projects'],
    queryFn: () => fixtureOr(FIXTURE_PROJECTS, () => apiGet<XcpProject[]>(ROUTES.projects)),
  });
}

export function useEvents(projectId?: string): UseQueryResult<Enveloped<XcpEvent[]>> {
  return useQuery({
    queryKey: ['events', projectId ?? 'all'],
    queryFn: () =>
      fixtureOr(
        projectId ? FIXTURE_EVENTS.filter((e) => e.project === projectId) : FIXTURE_EVENTS,
        () => apiGet<XcpEvent[]>(projectId ? ROUTES.projectEvents(projectId) : ROUTES.events),
      ),
  });
}

export function useSources(): UseQueryResult<Enveloped<XcpSource[]>> {
  return useQuery({
    queryKey: ['sources'],
    queryFn: () => fixtureOr(FIXTURE_SOURCES, () => apiGet<XcpSource[]>(ROUTES.sources)),
  });
}

export function useChats(): UseQueryResult<Enveloped<XcpChat[]>> {
  return useQuery({
    queryKey: ['chats'],
    queryFn: () => fixtureOr(FIXTURE_CHATS, () => apiGet<XcpChat[]>(ROUTES.cockpitChatHistory)),
  });
}

export function useThread(chatId: string | null): UseQueryResult<Enveloped<XcpMessage[]>> {
  return useQuery({
    enabled: !!chatId,
    queryKey: ['thread', chatId],
    queryFn: () =>
      fixtureOr(
        chatId ? FIXTURE_THREADS[chatId] ?? [] : [],
        () => apiGet<XcpMessage[]>(`${ROUTES.cockpitChatHistory}?chat_id=${chatId}`),
      ),
  });
}

export type EventVerdict = 'approved' | 'rejected' | 'noted';

export interface EventDecisionInput {
  eventId: string;
  verdict: EventVerdict;
  projectId?: string; // for cache invalidation of the rail
}

/**
 * Governed event decision (C6). POST /sign-offs {event_id, verdict} with a per-action Idempotency-Key
 * (§220-B). Optimistically re-states the event row (needsrev → approved/blocked) so the rail reflects
 * the verdict immediately; the caller gates this on spine-authority signoff:decide. In fixture mode it
 * only mutates the cache.
 */
export function useEventDecision(): UseMutationResult<void, Error, EventDecisionInput> {
  const qc = useQueryClient();
  return useMutation<void, Error, EventDecisionInput>({
    mutationFn: async ({ eventId, verdict, projectId }) => {
      const nextState = verdict === 'approved' ? 'approved' : verdict === 'rejected' ? 'blocked' : undefined;
      const patch = (rows?: Enveloped<XcpEvent[]>): Enveloped<XcpEvent[]> | undefined => {
        if (!rows) return rows;
        return {
          ...rows,
          data: rows.data.map((e) => (e.id === eventId && nextState ? { ...e, state: nextState } : e)),
        };
      };
      qc.setQueryData<Enveloped<XcpEvent[]>>(['events', projectId ?? 'all'], patch);
      qc.setQueryData<Enveloped<XcpEvent[]>>(['events', 'all'], patch);
      if (!USE_MSW) {
        await apiWrite('POST', ROUTES.signOffs, { event_id: eventId, verdict }, { idempotencyKey: newIdempotencyKey() });
      }
    },
  });
}

export interface SendMessageInput {
  chatId: string;
  text: string;
  mode?: string;
}

interface ChatTurnResponse {
  reply: string;
  echo?: XcpMessage['echo'];
}

/**
 * Governed composer send (C1). POST /customer-chat with a per-action Idempotency-Key (§220-B) so a
 * retry can't double-send. Optimistically appends the operator's turn to the thread cache, then the
 * assistant reply on success. In dev/MSW mode it echoes a canned reply so the surface is exercisable.
 * A real send never writes the record — chat is read-only about the record (grounding, not mutation).
 */
export function useSendMessage(): UseMutationResult<XcpMessage, Error, SendMessageInput> {
  const qc = useQueryClient();
  return useMutation<XcpMessage, Error, SendMessageInput>({
    mutationFn: async ({ chatId, text, mode }) => {
      const key = ['thread', chatId] as const;
      const prev = qc.getQueryData<Enveloped<XcpMessage[]>>(key)?.data ?? [];
      const nextSeq = prev.reduce((m, x) => Math.max(m, x.seq), 0) + 1;

      // Optimistic: show the operator's turn immediately.
      const youMsg: XcpMessage = { seq: nextSeq, role: 'you', text, when: 'now', mode };
      qc.setQueryData<Enveloped<XcpMessage[]>>(key, (cur) => ({
        data: [...(cur?.data ?? []), youMsg],
        data_class: cur?.data_class,
      }));

      let reply: ChatTurnResponse;
      if (USE_MSW) {
        reply = { reply: `Acknowledged: "${text.slice(0, 80)}". Grounded against this workspace; nothing was written to the record.` };
      } else {
        const res = await apiWrite<ChatTurnResponse>('POST', ROUTES.customerChat, { message: text, mode }, { idempotencyKey: newIdempotencyKey() });
        reply = res.data;
      }

      const asstMsg: XcpMessage = { seq: nextSeq + 1, role: 'asst', text: reply.reply, when: 'now', echo: reply.echo };
      qc.setQueryData<Enveloped<XcpMessage[]>>(key, (cur) => ({
        data: [...(cur?.data ?? []), asstMsg],
        data_class: cur?.data_class,
      }));
      return asstMsg;
    },
  });
}
